import { IonButton, IonCardContent, IonCardHeader, IonIcon, IonItem, IonRow } from '@ionic/react';
import classNames from 'classnames';
import { informationCircle, resize } from 'ionicons/icons';
import React, { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { selectIsMetric } from 'src/redux/profile-preferences/selectors';
import { convertUnit } from 'src/utils/helpers/chartHelpers';
import { JobLogV2Data } from 'src/utils/logs/types';
import { JobMessages } from '../../intl/JobMessages';
import { DqText } from '../dq-text';
import { ClampPressureModal } from './information/information-modals/ClampPressureModal';
import { JobClampPressureGraphTranslator } from './util/JobClampPressureGraphTranslator';
import { JobGraphMask, JobPressureGraphDataSegment } from './util/types';
import WaterPressureGraph, { WaterPressureGraphRef } from './water-pressure/WaterPressureGraph';
import { max } from 'lodash';

interface JobClampPressureGraphProps {
  /** Clamp pressure data */
  clampData: JobLogV2Data['clamp'] | undefined;
  /** The time table used to correlate events with their calendary time. */
  timeTable: JobLogV2Data['timeTable'] | undefined;
  /** Time range min value */
  timeRangeMin: number;
  /** Time range max value */
  timeRangeMax: number;
  /** The timezone associated with the job log. */
  timezone: string;
  showTimestamps: boolean;
}

const JobClampPressureGraph: FC<JobClampPressureGraphProps> = ({
  clampData,
  timeTable,
  timeRangeMin,
  timeRangeMax,
  showTimestamps,
  timezone,
}) => {
  const intl = useIntl();
  const [helpDialogOpen, setHelpDialogOpen] = React.useState<boolean>(false);
  const graphRef = React.useRef<WaterPressureGraphRef | null>(null);
  const [isZoomed, setZoomed] = React.useState(false);
  const isMetric = useSelector(selectIsMetric);

  /** The view model data for the pressure graph. */
  const graphData = React.useMemo<JobPressureGraphDataSegment>(() => {
    if (!clampData || !timeTable) return [];
    return JobClampPressureGraphTranslator.parseDataV2(clampData, timeTable, timeRangeMin, timeRangeMax, isMetric);
  }, [clampData, timeTable, timeRangeMin, timeRangeMax, isMetric]);

  const masks = React.useMemo<JobGraphMask[]>(() => {
    const maxPressure = max(graphData.map(i => i.pressure)) ?? 30;
    const yMax = Number.parseFloat(convertUnit(maxPressure, 'pressure', isMetric, 3));
    const yMin = Number.parseFloat(convertUnit(10, 'pressure', isMetric, 3));
    const mask: JobGraphMask = {
      type: 'safe-y',
      yMax,
      yMin,
    };
    return [mask];
  }, [graphData, isMetric]);

  const hideTimestamp = !showTimestamps;

  return (
    <>
      <IonCardHeader>
        <IonRow className="ion-justify-content-between ion-align-items-center">
          <DqText variant="h3">{intl.formatMessage(JobMessages.clampPressureLabel)}</DqText>
          <div>
            {isZoomed && (
              <IonButton fill="clear" color="dark" onClick={graphRef.current?.resetZoom}>
                <IonIcon slot="icon-only" icon={resize} />
              </IonButton>
            )}
            <IonButton fill="clear" color="primary" onClick={() => setHelpDialogOpen(true)}>
              <IonIcon slot="icon-only" icon={informationCircle} />
            </IonButton>
          </div>
        </IonRow>
      </IonCardHeader>
      <IonCardContent>
        {!!graphData && graphData.length ? (
          <div className={classNames(!showTimestamps && 'graph-timestamp-no-print graph-timestamp-hide')}>
            <WaterPressureGraph
              ref={graphRef}
              data={graphData}
              hideTimestamps={hideTimestamp}
              masks={masks}
              onZoom={setZoomed}
              timezone={timezone}
            />
          </div>
        ) : (
          <IonItem className="ion-justify-content-center">
            <FormattedMessage {...JobMessages.clampPressureDataEmpty} />
          </IonItem>
        )}
      </IonCardContent>
      <ClampPressureModal isOpen={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} />
    </>
  );
};

export default JobClampPressureGraph;
